import React from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaExclamationTriangle } from 'react-icons/fa';
import PropTypes from 'prop-types';

/**
 * RAT-1828: last check before a bulk invoice action runs. It restates what is
 * about to happen and for which merchants, so a wrong period or merchant set is
 * caught before any invoice is issued. Every action is confirmed, Preview
 * included.
 *
 * Stacked on top of the Invoice Generation dialog: cancelling (button, Esc, or
 * overlay click) only closes this one, leaving the configuration untouched.
 */

const SummaryRow = ({ label, value }) => (
  <Flex gap={3} align="flex-start">
    <Text fontSize="sm" color="gray.500" minW="110px">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="600" color="gray.800">
      {value}
    </Text>
  </Flex>
);
SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

const InvoiceConfirmationDialog = ({
  isOpen,
  onCancel,
  onConfirm,
  isBusy,
  actionLabel,
  periodLabel,
  languageLabel,
  scopeLabel,
  merchantStatusLabel,
  merchants,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onCancel}
    isCentered
    scrollBehavior="inside"
    size={{ base: 'sm', md: 'lg' }}
  >
    <ModalOverlay />
    <ModalContent
      mx={{ base: 3, md: 4 }}
      maxH={{ base: '92vh', md: '88vh' }}
      borderRadius={{ base: '12px', md: '16px' }}
    >
      <ModalHeader display="flex" gap={3} alignItems="flex-start" pb={3}>
        <Icon
          as={FaExclamationTriangle}
          color="orange.400"
          w="20px"
          h="20px"
          mt="2px"
        />
        <Box>
          <Text fontSize="lg" fontWeight="700">
            Confirm {actionLabel}
          </Text>
          <Text fontSize="sm" fontWeight="400" color="gray.500">
            Review the details below before this action runs.
          </Text>
        </Box>
      </ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      {/* The dialog underneath keeps a scroll lock that listens for wheel
            events on the document and cancels every one raised outside its own
            content. This dialog is portalled to the body, so it counts as
            "outside" and its scrollable areas would only respond to dragging the
            scrollbar. Stopping the event here keeps it from ever reaching that
            listener, so the browser scrolls normally; overscrollBehavior then
            stops the wheel carrying on to the page once a list hits its end. */}
      <ModalBody
        pt={0}
        px={{ base: 4, md: 6 }}
        overflowY="auto"
        overscrollBehavior="contain"
        onWheel={(event) => event.stopPropagation()}
      >
        <VStack spacing={4} align="stretch">
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="12px"
            px={4}
            py={3}
          >
            <VStack spacing={2} align="stretch">
              <SummaryRow label="Action" value={actionLabel} />
              <SummaryRow label="Period" value={periodLabel} />
              <SummaryRow label="Language" value={languageLabel} />
              <SummaryRow label="Merchant status" value={merchantStatusLabel} />
              <SummaryRow label="Scope" value={scopeLabel} />
            </VStack>
          </Box>

          <Box>
            <Flex justify="space-between" align="baseline" mb={2}>
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                Merchants included
              </Text>
              <Text fontSize="xs" color="gray.500">
                {merchants.length}{' '}
                {merchants.length === 1 ? 'merchant' : 'merchants'}
              </Text>
            </Flex>
            {/* Scrollable so a large selection never pushes the footer out of
                reach - the count above always states the full total. */}
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="12px"
              maxH="220px"
              overflowY="auto"
              // Keeps the wheel from carrying on to the page behind once the
              // list has reached its end.
              overscrollBehavior="contain"
              px={4}
              py={3}
            >
              <List spacing={1} styleType="disc" pl={4}>
                {merchants.map((merchant) => (
                  <ListItem
                    key={merchant.merchantId}
                    fontSize="sm"
                    color="gray.800"
                  >
                    {merchant.displayName || merchant.name}
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>

          <Divider />

          <Text fontSize="sm" fontWeight="600" color="gray.700">
            Do you want to proceed with this action?
          </Text>
        </VStack>
      </ModalBody>
      <ModalFooter
        gap={3}
        flexDirection={{ base: 'column-reverse', sm: 'row' }}
        px={{ base: 4, md: 6 }}
      >
        <Button
          type="button"
          variant="outline"
          fontSize="sm"
          fontWeight="500"
          minW="120px"
          w={{ base: '100%', sm: 'auto' }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          bg="primary"
          color="white"
          _hover={{ bg: 'primary', opacity: 0.8 }}
          isLoading={isBusy}
          loadingText="Starting..."
          isDisabled={isBusy}
          fontSize="sm"
          fontWeight="500"
          minW="160px"
          w={{ base: '100%', sm: 'auto' }}
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

InvoiceConfirmationDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isBusy: PropTypes.bool,
  actionLabel: PropTypes.string.isRequired,
  periodLabel: PropTypes.string.isRequired,
  languageLabel: PropTypes.string.isRequired,
  scopeLabel: PropTypes.string.isRequired,
  merchantStatusLabel: PropTypes.string.isRequired,
  merchants: PropTypes.array.isRequired,
};

export default InvoiceConfirmationDialog;
